import { api } from '@/lib/api/endpoints';
import { buildUrl, sessionHeaders, ApiError } from '@/lib/api/client';
import { logError, logInfo } from '@/lib/log';
import type { ExportExtra, ExportJob } from '@/lib/api/types';

/** Start a browser download of a (signed, public) URL. */
export function triggerDownload(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Download an API resource that needs the bearer token (e.g. transcript SRT) via fetch → blob. */
export async function downloadAuthed(path: string, filename: string): Promise<void> {
  const url = buildUrl(path);
  const res = await fetch(url, { headers: sessionHeaders() });
  if (!res.ok) {
    logError('download', `GET ${path} → ${res.status}`);
    throw new ApiError(res.status, res.statusText || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  triggerDownload(href, filename);
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/** Fetch a fresh signed URL for a render (signed URLs expire), then download it. */
export async function downloadRender(renderId: string, kind: 'mp4' | 'srt', filename: string): Promise<void> {
  try {
    const r = await api.pipeline.getRender(renderId);
    const url = kind === 'mp4' ? r.url : r.srt_url;
    if (!url) throw new ApiError(409, 'The render is not ready yet');
    logInfo('download', `render=${renderId} kind=${kind}`);
    triggerDownload(url, filename);
  } catch (e) {
    logError('download', `render=${renderId} kind=${kind} failed`, e);
    throw e;
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Editor timelines for Premiere (xmeml), Resolve / Final Cut (FCPXML) and any NLE (CMX3600), plus SRT captions. */
export const TIMELINE_EXTRAS: ExportExtra[] = ['fcpxml', 'xmeml', 'edl', 'srt'];

export interface PackOptions {
  extras?: ExportExtra[];
  /** false = timelines only: no MP4s, and the edits need no finished render. */
  includeVideo?: boolean;
  pollMs?: number;
}

/** Create a ZIP pack (MP4 + SRT + caption text, or editor timelines) and poll until it is ready (≤ 5 min), then download it. */
export async function downloadPack(editIds: string[], onState?: (s: ExportJob['status']) => void, opts: PackOptions | number = {}): Promise<void> {
  const o: PackOptions = typeof opts === 'number' ? { pollMs: opts } : opts;
  const pollMs = o.pollMs ?? 2000;
  let job = await api.pipeline.createExport(editIds, o.extras, o.includeVideo ?? true);
  logInfo('download', `export=${job.id} edits=${editIds.length} extras=${(o.extras ?? ['default']).join(',')} video=${o.includeVideo ?? true}`);
  const deadline = Date.now() + 5 * 60_000;
  while (!job.url) {
    onState?.(job.status);
    if (job.status === 'failed') {
      logError('download', `export=${job.id} failed`);
      throw new ApiError(500, 'The ZIP export failed');
    }
    if (Date.now() > deadline) {
      logError('download', `export=${job.id} timed out`);
      throw new ApiError(504, 'The ZIP export is taking too long. Try again later.');
    }
    await sleep(pollMs);
    job = await api.pipeline.getExport(job.id);
  }
  onState?.('ready');
  triggerDownload(job.url, `castfeeds-${job.id}.zip`);
}

/** File-system-safe name for downloads. */
export function safeName(s: string): string {
  return s.replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '-').slice(0, 80) || 'castfeeds';
}
